import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Code, Monitor, Smartphone, BarChart3, Server, Shield, Zap, Globe } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Technology() {
  const { t } = useLanguage();

  const features = [
    { icon: Code, title: t('technology.features.api.title'), desc: t('technology.features.api.desc') },
    { icon: Monitor, title: t('technology.features.platform.title'), desc: t('technology.features.platform.desc') },
    { icon: Smartphone, title: t('technology.features.mobile.title'), desc: t('technology.features.mobile.desc') },
    { icon: BarChart3, title: t('technology.features.analytics.title'), desc: t('technology.features.analytics.desc') },
  ];

  const techStack = [
    { icon: Server, label: 'API REST' },
    { icon: Shield, label: 'SSL/TLS' },
    { icon: Zap, label: 'Real-time' },
    { icon: Globe, label: 'Cloud' },
  ];

  const stats = [
    { value: '3.5M+', label: t('technology.stats.requests') },
    { value: '10K+', label: t('technology.stats.bookings') },
    { value: '99.9%', label: t('technology.stats.uptime') },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="tech-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('technology.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('technology.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-testid="tech-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Inovação Tecnológica
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {t('technology.intro')}
              </p>
              <div className="flex flex-wrap gap-4">
                {techStack.map((tech, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full"
                  >
                    <tech.icon className="w-5 h-5 text-[#1E3A8A]" />
                    <span className="text-slate-700 font-medium">{tech.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] p-1">
                <div className="w-full h-full bg-[#0F172A] rounded-3xl flex items-center justify-center">
                  <div className="text-center p-8">
                    <Code className="w-24 h-24 text-[#00D4FF] mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {t('technology.statsTitle')}
                    </h3>
                    <p className="text-slate-400">
                      Infraestrutura robusta e escalável
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="tech-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Soluções Tecnológicas
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="group p-8 bg-white rounded-3xl border border-slate-100 hover:border-[#00D4FF]/30 hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`tech-feature-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="tech-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('technology.statsTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-3xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-5xl font-bold text-[#00D4FF] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-300 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Info */}
      <section className="py-24 bg-white" data-testid="tech-integration">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1"
              {...fadeInUp}
            >
              <div className="bg-[#0F172A] rounded-3xl p-8 font-mono text-sm overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <pre className="text-slate-300 overflow-x-auto">
{`// API Integration Example
const response = await fetch(
  'https://api.7x7travel.pt/v1/hotels',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      destination: 'Lisboa',
      checkIn: '2024-06-15',
      checkOut: '2024-06-20',
      guests: 2
    })
  }
);

const hotels = await response.json();`}
                </pre>
              </div>
            </motion.div>

            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Integração Simples
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                A nossa API foi desenhada para ser simples e intuitiva. Com documentação completa e suporte técnico dedicado, a integração com os seus sistemas é rápida e eficiente.
              </p>
              <ul className="space-y-3">
                {['Documentação completa', 'Suporte técnico dedicado', 'Sandbox para testes', 'Webhooks em tempo real'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <div className="w-2 h-2 rounded-full bg-[#00D4FF]" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
